// Improvements:
// --> Include a Server side virus scan
// --> MIME sniffing to double check file content matches MIME type
// --> Rate limiting uploads per IP/session
// --> Upload progress bar per file.
// --> Cancel upload per individual file
// TODO: --> Implement batch queue
// --> Lazy load media previews
// --> Add mulit-store scoping
// --> Add a image history log for revert function, audits and debugging.
// --> Move inline error messages to a constants file
// --> Sanitize against long file names
// --> Add capture:environment for mobile interfaces.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Uppy from "@uppy/core";
import GoldenRetriever from "@uppy/golden-retriever";

import { useImageManager } from "@/hooks/useImageManager";
import { generateCustomId } from "@/utils/generateCustomId";
import { getCachedImageUrl, setCachedImageUrl } from "@/utils/image-central-manager/tempImageCache";

// keep react-dropzone just for UI wiring (open dialog + drop area)
import { useDropzone } from "react-dropzone";

// TODO: Are these values redundant?
const DEFAULT_MAX_MB = 2;
const DEFAULT_ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const extensionWhitelist = /\.(jpe?g|png)$/i;

function buildImageMeta({ file, altBase }) {
  return new Promise((resolve) => {
    const baseAlt = altBase || file.name.replace(/\.[^/.]+$/, "");
    const img = new Image();

    const existingUrl = getCachedImageUrl(baseAlt);
    // TODO: Use a signed URL from the backend
    const objectUrl = existingUrl || URL.createObjectURL(file);
    // Persist the blob URL in a session cache so the UI can reuse it
    // across modal open/close cycles without breaking the reference.
    if (!existingUrl) setCachedImageUrl(baseAlt, objectUrl);

    img.onload = () => {
      resolve({ 
        objectUrl, 
        width: img.naturalWidth, 
        height: img.naturalHeight,
        // TODO: Wont need this once we connect to a signed URL
        // Important: Do not revoke here if we cached the URL, otherwise
        // subsequent renders will fail with net::ERR_FILE_NOT_FOUND.
        // Cleanup is handled when images are explicitly removed/cleared.
        cleanup: () => {},
      });
    };

    img.onerror = () => {
      resolve({ objectUrl: "", width: 0, height: 0, cleanup: () => {} });
    };

    img.src = objectUrl;
  });
}

// Create a unique, human-friendly base name using an underscore suffix
function makeUniqueBaseName(base, takenSet) {
  let candidate = base;
  let i = 1;
  while (takenSet.has(candidate.toLowerCase())) {
    candidate = `${base}_${i}`;
    i++;
  }
  takenSet.add(candidate.toLowerCase());
  return candidate;
}

function splitName(name = "") {
  const base = name.replace(/\.[^/.]+$/, "");
  const extMatch = name.match(/\.([^.]+)$/);
  const ext = extMatch ? extMatch[1] : "";
  return { base, ext };
}

export function useUppyDropzoneCore({
  onFilesProcessed,
  existingAlts = [],
  setError,
  clearErrors,
  dropzoneRef,
  maxSizeMB = DEFAULT_MAX_MB,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  validate = true,
  exposeOpen = true,
  attachUploader,
  onUploadStart,
  onUploadSuccess,
  onUploadError,
  onUploadComplete,
}) {
  const { addImages, images } = useImageManager();
  const uppyRef = useRef(null);
  const existingAltsRef = useRef(existingAlts);
  const uploadingCountRef = useRef(0);
  const isUploadingRef = useRef(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  // TODO: Is this value redundant.
  const MAX_FILE_SIZE = maxSizeMB * 1024 * 1024;

  // Throttle the number of buildImageMeta calls run concurrently to avoid memory overload
  function createAsyncThrottle(limit = 3) {
    let active = 0;
    const queue = [];

    const next = () => {
      if (queue.length && active < limit) {
        const task = queue.shift();
        active++;
        task().finally(() => {
          active--;
          next();
        });
      }
    };

    return (fn) =>
      new Promise((resolve, reject) => {
        queue.push(() =>
          fn().then(resolve).catch(reject)
        );
        next();
      });
  }

  // Stable list for allowed types mapping used by both react-dropzone and Uppy.restrictions
  const acceptMap = useMemo(() => {
    return allowedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {});
  }, [allowedTypes]);

  // Create and configure Uppy instance
  useEffect(() => {
    const uppy = new Uppy({
      autoProceed: false,
      allowMultipleUploads: true,
      // ensure duplicates are allowed; we also namespace IDs to avoid collisions
      onBeforeFileAdded: (currentFile, files) => {
        // Nothing blocked; allow duplicates by tweaking the internal ID space with timestamp
        const uniqueId = `${currentFile.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        return { ...currentFile, id: uniqueId };
      },
      restrictions: {
        allowedFileTypes: allowedTypes,
        maxFileSize: validate ? MAX_FILE_SIZE : null,
        maxNumberOfFiles: null, // unlimited
      },
    });

    // Persist across refreshes/restores
    uppy.use(GoldenRetriever, { serviceWorker: false });

    // Use a custom uploader to an actual cloud storage.
    if (typeof attachUploader === "function") {
      attachUploader(uppy);
    }

    // Handle file batches when multiple files are added together
    uppy.on("files-added", async (files) => {
      const errors = [];
      // upload start
      try {
        uploadingCountRef.current = files.length;
        isUploadingRef.current = true;
        setUploadingCount(files.length);
        setIsUploading(true);
        onUploadStart && onUploadStart(files.length);
      } catch {}

      // Build a set of already taken base names from:
      // - provided existingAlts
      // - current global image store (if used inside the modal)
      const initialTaken = new Set([
        ...(Array.isArray(existingAltsRef.current) ? existingAltsRef.current : []),
        ...((Array.isArray(images) ? images : [])
          .map((img) => {
            if (img?.name) return img.name.replace(/\.[^/.]+$/, "");
            if (img?.alt) return String(img.alt);
            return null;
          })
          .filter(Boolean)),
      ].map((s) => String(s).toLowerCase()));

      const results = await Promise.all(
        files.map(async (f) => {
          const file = f.data;

          // Validate here too (react-dropzone also restricts input, but files can be restored by GoldenRetriever)
          if (validate) {
            const typeOk = allowedTypes.includes(file.type);
            
            // Validate the file extension
            
            const extensionOk = extensionWhitelist.test(file.name);
            if (!typeOk || !extensionOk) {
              errors.push(`${file.name} has an unsupported file type.`);
              return null;
            }
            const sizeOk = file.size <= MAX_FILE_SIZE;
            if (!typeOk) {
              errors.push(`${file.name} is not a supported image format.`);
              return null;
            }
            if (!sizeOk) {
              errors.push(`${file.name} exceeds the maximum size of ${maxSizeMB}MB.`);
              return null;
            }
          }

          // Ensure alt uniqueness relative to supplied existingAlts
          const { base, ext } = splitName(file.name || "image");
          const finalBase = makeUniqueBaseName(base, initialTaken);
          const finalAlt = finalBase;

          const meta = await buildImageMeta({ file, altBase: finalAlt });

          // Warn if dimensions are invalid
          if (!meta.width || !meta.height) {
            console.warn("Image has zero dimensions:", file.name);
            errors.push(`${file.name} has unreadable or corrupt image dimensions.`);
            return null;
          }

          // No-op: cleanup is managed centrally when removing/clearing images

          // Freeze the object to prevent accidental mutations
          const frozenMeta = Object.freeze({
            size: file.size,
            type: file?.type || "application/octet-stream",
            width: meta.width,
            height: meta.height,
            createdAt: new Date().toISOString(),
          });

          return {
            // A unique temporary frontend id necessary for UI logic.
            // Isnt pushed to the backend.
            // TODO: Add schema validation for uploaded image object before pushing to store
            instanceId: generateCustomId(),
            id: undefined,
            url: meta.objectUrl,
            name: `${finalBase}${ext ? "." + ext : ""}`.slice(0, 120),
            alt: finalAlt,
            file,
            tags: [],
            meta: frozenMeta,
          };
        })
      );

      // simulate a short delay so spinners are visible
      // TODO: Remove this fake delay
      try { await new Promise((r) => setTimeout(r, 900)); } catch {}

      const validImages = results.filter(Boolean);

      if (validImages.length) {
        const areValid = validImages.every(img =>
          typeof img.instanceId === "string" &&
          typeof img.name === "string" &&
          typeof img.url === "string"
        );
        if (!areValid) {
          console.warn("Invalid image structure found in upload batch:", validImages);
          return;
        }

        // Add valid images to global store and notify listeners
        addImages(validImages);
        try { onUploadSuccess && onUploadSuccess(validImages.length); } catch {}

        if (typeof onFilesProcessed === "function") {
          onFilesProcessed(validImages);
        }
      }

      // Relay errors to form layer
      if (errors.length > 0 && setError) {
        setError("productImages", { type: "manual", message: errors.join("\n") });
        try { onUploadError && onUploadError(errors); } catch {}
      } else if (clearErrors) {
        clearErrors("productImages");
      }

      try { onUploadComplete && onUploadComplete({ successCount: validImages.length, errors }); } catch {}

      // upload end
      isUploadingRef.current = false;
      uploadingCountRef.current = 0;
      setUploadingCount(0);
      setIsUploading(false);
    });

    // On restore, push any remembered files into the image store so UI stays in sync
    uppy.on("restore-success", async () => {
      const files = uppy.getFiles();
      if (!files?.length) return;

      // Build initial taken set from store to keep restored names unique
      const taken = new Set([
        ...((Array.isArray(images) ? images : [])
          .map((img) => {
            if (img?.name) return img.name.replace(/\.[^/.]+$/, "");
            if (img?.alt) return String(img.alt);
            return null;
          })
          .filter(Boolean)),
      ].map((s) => String(s).toLowerCase()));

      const images = await Promise.all(
        files.map(async (f) => {
          const file = f.data;
          const { base, ext } = splitName(file.name || "image");
          const baseAlt = makeUniqueBaseName(base, taken);
          // Throttled to 3 calls at a time
          const runThrottled = createAsyncThrottle(3);
          const meta = await runThrottled(() => buildImageMeta({ file, altBase: baseAlt }));
          
          const frozenMeta = Object.freeze({
            size: file.size,
            type: file?.type || "application/octet-stream",
            width: meta.width,
            height: meta.height,
            createdAt: new Date().toISOString(),
          });

          return {
            // A unique temporary frontend id necessary for UI logic.
            // Isnt pushed to the backend.
            // TODO: Add schema validation for uploaded image object before pushing to store
            instanceId: generateCustomId(),
            id: undefined,
            url: meta.objectUrl,
            name: `${baseAlt}${ext ? "." + ext : ""}`,
            alt: baseAlt,
            file,
            tags: [],
            meta: frozenMeta,
          };
        })
      );
    
      if (images.length) addImages(images);
    });

    uppyRef.current = uppy;

    return () => {
      // Uppy uses destroy() to tear down the instance
      if (uppy && typeof uppy.destroy === "function") {
        uppy.destroy();
      }
      uppyRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validate, MAX_FILE_SIZE, allowedTypes.join(","), maxSizeMB, addImages]);

  // Keep a fresh copy of existingAlts without recreating the Uppy instance
  useEffect(() => {
    existingAltsRef.current = existingAlts;
  }, [existingAlts.join("|")]);

  // react-dropzone for UI: it only collects files then we forward to Uppy
  const onDrop = useCallback(
    (acceptedFiles) => {
      const uppy = uppyRef.current;
      if (!uppy || !acceptedFiles?.length) return;

      acceptedFiles.forEach((file) => {
        if (file?.name && file?.type) {
          uppy.addFile({
            data: file,
            name: file.name,
            type: file.type,
          });
        }
      });
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: acceptMap,
    maxSize: validate ? MAX_FILE_SIZE : undefined,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  });

  // Expose the 'open' function to parent components
  useEffect(() => {
    if (!dropzoneRef || !exposeOpen) return;
    const t = setTimeout(() => {
      if (exposeOpen && typeof dropzoneRef.current !== "function") {
        dropzoneRef.current = open;
      }
    }, 0);
    return () => clearTimeout(t);
  }, [open, dropzoneRef, exposeOpen]);

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    open,
    uppy: uppyRef,
    isUploading,
    uploadingCount,
  };
}
